/**
 * Per-viewer comfort settings (docs/SPEC.md §5.4), applied as attributes on <html>:
 *
 *   data-theme    galley | stacks | museum | conservatory   (the current galley's theme)
 *   data-mode     day | night | projector | contrast
 *   data-calm     present when motion should stop
 *   data-reading  standard | spacious | wide
 *
 * Sources, strongest first: the projector route (always projector + calm) → the viewer's own
 * choice (localStorage 'galley.comfort'; students also sync it to the server) → the device
 * (prefers-reduced-motion, prefers-color-scheme, prefers-contrast) → the class default
 * (calmDefault) → Galley's defaults.
 */
import type { ColorMode, ComfortSettings, ReadingStyle, ThemeId } from '@galley/shared'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react'
import { loadWideFont } from '../design/fonts'
import { surfaceColor } from '../design/tokens'

export const COMFORT_STORAGE_KEY = 'galley.comfort'

export const DEFAULT_COMFORT: ComfortSettings = {
  calm: null,
  night: null,
  readingStyle: 'standard',
  contrast: false,
}

const READING_STYLES: readonly ReadingStyle[] = ['standard', 'spacious', 'wide']

export interface ResolvedComfort {
  theme: ThemeId
  mode: ColorMode
  calm: boolean
  reading: ReadingStyle
  /** Individual facts, for showing why a switch is on. */
  night: boolean
  contrast: boolean
  deviceContrast: boolean
  projector: boolean
}

interface GalleyComfort {
  theme: ThemeId
  calmDefault: boolean
}

interface ComfortContextValue {
  prefs: ComfortSettings
  resolved: ResolvedComfort
  setPrefs: (patch: Partial<ComfortSettings>) => void
  /** Forget this viewer's choices and follow the device again. */
  resetPrefs: () => void
  /** Replace all choices at once (e.g. a student's saved settings after sign-in). */
  adoptPrefs: (prefs: ComfortSettings) => void
  registerGalley: (key: symbol, galley: GalleyComfort | null) => void
  registerProjector: (key: symbol, active: boolean) => void
}

const ComfortContext = createContext<ComfortContextValue | null>(null)

function isNullableBool(value: unknown): value is boolean | null {
  return value === null || typeof value === 'boolean'
}

/** Parse stored settings defensively: storage is user-editable and may be from an old version. */
export function parseComfort(raw: string | null): ComfortSettings {
  if (!raw) return DEFAULT_COMFORT
  try {
    const value = JSON.parse(raw) as Partial<Record<keyof ComfortSettings, unknown>>
    return {
      calm: isNullableBool(value.calm) ? value.calm : null,
      night: isNullableBool(value.night) ? value.night : null,
      readingStyle: READING_STYLES.includes(value.readingStyle as ReadingStyle)
        ? (value.readingStyle as ReadingStyle)
        : 'standard',
      contrast: value.contrast === true,
    }
  } catch {
    return DEFAULT_COMFORT
  }
}

function readStored(): ComfortSettings {
  try {
    return parseComfort(localStorage.getItem(COMFORT_STORAGE_KEY))
  } catch {
    return DEFAULT_COMFORT
  }
}

function writeStored(prefs: ComfortSettings | null): void {
  try {
    if (prefs) localStorage.setItem(COMFORT_STORAGE_KEY, JSON.stringify(prefs))
    else localStorage.removeItem(COMFORT_STORAGE_KEY)
  } catch {
    // Settings still apply for this visit.
  }
}

/** Subscribe to a CSS media query; false where matchMedia is unavailable. */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof window.matchMedia !== 'function') return () => {}
      const list = window.matchMedia(query)
      list.addEventListener('change', onChange)
      return () => list.removeEventListener('change', onChange)
    },
    [query],
  )
  return useSyncExternalStore(
    subscribe,
    () => (typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : false),
    () => false,
  )
}

export function ComfortProvider({ children }: { children: ReactNode }) {
  const [prefs, setPrefsState] = useState<ComfortSettings>(readStored)
  const [galleys, setGalleys] = useState<ReadonlyMap<symbol, GalleyComfort>>(new Map())
  const [projectors, setProjectors] = useState<ReadonlySet<symbol>>(new Set())

  const osReduce = useMediaQuery('(prefers-reduced-motion: reduce)')
  const osDark = useMediaQuery('(prefers-color-scheme: dark)')
  const osMore = useMediaQuery('(prefers-contrast: more)')

  const setPrefs = useCallback((patch: Partial<ComfortSettings>) => {
    setPrefsState((current) => {
      const next = { ...current, ...patch }
      writeStored(next)
      return next
    })
  }, [])

  const resetPrefs = useCallback(() => {
    writeStored(null)
    setPrefsState(DEFAULT_COMFORT)
  }, [])

  const adoptPrefs = useCallback((next: ComfortSettings) => {
    writeStored(next)
    setPrefsState(next)
  }, [])

  const registerGalley = useCallback((key: symbol, galley: GalleyComfort | null) => {
    setGalleys((current) => {
      const next = new Map(current)
      if (galley) next.set(key, galley)
      else next.delete(key)
      return next
    })
  }, [])

  const registerProjector = useCallback((key: symbol, active: boolean) => {
    setProjectors((current) => {
      const next = new Set(current)
      if (active) next.add(key)
      else next.delete(key)
      return next
    })
  }, [])

  const resolved = useMemo<ResolvedComfort>(() => {
    // The most recently registered galley wins (a screen inside a shell can refine it).
    const galley = [...galleys.values()].at(-1)
    const projector = projectors.size > 0
    const contrast = prefs.contrast || osMore
    const night = prefs.night ?? osDark
    const mode: ColorMode = projector
      ? 'projector'
      : contrast
        ? 'contrast'
        : night
          ? 'night'
          : 'day'
    const calm = projector || (prefs.calm ?? (osReduce || (galley?.calmDefault ?? false)))
    return {
      theme: galley?.theme ?? 'galley',
      mode,
      calm,
      reading: prefs.readingStyle,
      night,
      contrast,
      deviceContrast: osMore,
      projector,
    }
  }, [prefs, galleys, projectors, osReduce, osDark, osMore])

  useEffect(() => {
    const root = document.documentElement
    root.dataset.theme = resolved.theme
    root.dataset.mode = resolved.mode
    root.dataset.reading = resolved.reading
    if (resolved.calm) root.dataset.calm = ''
    else delete root.dataset.calm
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', surfaceColor(resolved.theme, resolved.mode))
  }, [resolved])

  useEffect(() => {
    if (resolved.reading === 'wide') void loadWideFont()
  }, [resolved.reading])

  const value = useMemo<ComfortContextValue>(
    () => ({
      prefs,
      resolved,
      setPrefs,
      resetPrefs,
      adoptPrefs,
      registerGalley,
      registerProjector,
    }),
    [prefs, resolved, setPrefs, resetPrefs, adoptPrefs, registerGalley, registerProjector],
  )

  return <ComfortContext.Provider value={value}>{children}</ComfortContext.Provider>
}

export function useComfort(): ComfortContextValue {
  const ctx = useContext(ComfortContext)
  if (!ctx) throw new Error('useComfort must be used inside <ComfortProvider>')
  return ctx
}

/**
 * Screens inside a galley call this with the galley's theme and class Calm default
 * (from WorldDTO.galley or GalleyDetailDTO). Pass null/undefined while it is loading.
 */
export function useGalleyComfort(
  galley: { theme: ThemeId; calmDefault?: boolean } | null | undefined,
): void {
  const { registerGalley } = useComfort()
  const [key] = useState(() => Symbol('galley'))
  const theme = galley?.theme
  const calmDefault = galley?.calmDefault ?? false
  useEffect(() => {
    registerGalley(key, theme ? { theme, calmDefault } : null)
    return () => registerGalley(key, null)
  }, [registerGalley, key, theme, calmDefault])
}

/** While mounted: projector palette, projector type scale and Calm motion, whatever the prefs. */
export function useProjectorMode(active = true): void {
  const { registerProjector } = useComfort()
  const [key] = useState(() => Symbol('projector'))
  useEffect(() => {
    registerProjector(key, active)
    return () => registerProjector(key, false)
  }, [registerProjector, key, active])
}
